import { Box, Flex, Popover, PopoverRootProps, Portal } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../Button';
import { CloseButton } from '../CloseButton';
import { IconButton } from '../IconButton';
import { PopoverContent } from '../Popover';

type HelpPopoverProps = PopoverRootProps & {
  children: React.ReactNode;
  triggerProps?: Popover.TriggerProps;
  /** The field name, used as the title and accessible trigger name. */
  accessibleLabel?: string;
  /** A visible text action for explanations that need more context than a question mark. */
  triggerLabel?: string;
  /** Catalog helpers contain trusted HTML; named help historically renders plain text. */
  renderAsHtml?: boolean;
  /** Inline field help keeps the glyph by the label and a full target on touch screens. */
  compact?: boolean;
  /** Put help after text in a wrapping heading's inline flow. */
  inline?: boolean;
};

export const HelpPopover = ({
  children,
  triggerProps,
  accessibleLabel,
  triggerLabel,
  renderAsHtml = !accessibleLabel,
  compact = false,
  inline = false,
  ...props
}: HelpPopoverProps) => {
  const { t } = useLingui();
  const title = accessibleLabel || t`Help`;
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const portalContainer = useMemo(() => ({ current: container }), [container]);
  useEffect(() => {
    // Keep help in the current dialog's focus scope without opening another modal.
    setContainer(
      triggerRef.current?.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ?? null
    );
  }, []);

  return (
    <Popover.Root
      positioning={{
        offset: { crossAxis: 0, mainAxis: 8 },
        strategy: 'fixed',
        overflowPadding: 16,
        flip: true,
        slide: true,
        fitViewport: true,
      }}
      autoFocus
      modal={false}
      lazyMount
      unmountOnExit
      {...props}
    >
      <Popover.Trigger asChild {...triggerProps}>
        {triggerLabel ? (
          <Button
            ref={triggerRef}
            variant="plain"
            color="fg.link"
            textStyle="caption"
            minH={11}
            h="auto"
            maxW="full"
            py={2}
            whiteSpace="normal"
          >
            {triggerLabel}
          </Button>
        ) : (
          <IconButton
            ref={triggerRef}
            variant="ghost"
            size="sm"
            bg="transparent"
            color="fg.muted"
            _hover={{ bg: 'transparent', color: 'fg' }}
            borderRadius="full"
            boxSize={compact ? { base: 11, md: 5 } : 11}
            minW={compact ? { base: 11, md: 5 } : 11}
            minH={compact ? { base: 11, md: 5 } : 11}
            alignItems={inline ? 'center' : compact ? 'flex-end' : undefined}
            justifyContent={compact ? 'flex-start' : undefined}
            css={
              compact
                ? { '@media (any-pointer: coarse)': { boxSize: 11, minW: 11, minH: 11 } }
                : undefined
            }
            flexShrink={0}
            p={0}
            textStyle="micro"
            aria-label={t`Help: ${title}`}
          >
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              boxSize="4.5"
              position={inline ? 'relative' : undefined}
              top={inline ? '-px' : undefined}
              bg="whiteAlpha.200"
              borderWidth="1px"
              borderColor="whiteAlpha.300"
              borderRadius="full"
              aria-hidden="true"
            >
              ?
            </Box>
          </IconButton>
        )}
      </Popover.Trigger>
      <Portal container={portalContainer}>
        <PopoverContent
          portalled={false}
          aria-label={title}
          bg="bg.raised"
          color="fg"
          w="sm"
          maxW="min(calc(100vw - 32px), var(--available-width, 100vw))"
          maxH="min(var(--available-height, 70dvh), 70dvh)"
          minW={0}
          overflow="hidden"
        >
          <Flex align="center" gap={2} ps={4} pe={2} py={2} flexShrink={0}>
            <Popover.Title textStyle="title" minW={0} flex={1} overflowWrap="anywhere">
              {title}
            </Popover.Title>
            <Popover.CloseTrigger asChild aria-label={t`Close`}>
              <CloseButton size="sm" boxSize={11} minW={11} minH={11} flexShrink={0} />
            </Popover.CloseTrigger>
          </Flex>
          <Popover.Body
            minH={0}
            overflowY="auto"
            overscrollBehavior="contain"
            p={4}
            pt={1}
            textStyle="prose"
          >
            {typeof children === 'string' ? (
              renderAsHtml ? (
                <Box dangerouslySetInnerHTML={{ __html: children }} whiteSpace="pre-wrap" />
              ) : (
                <Box whiteSpace="pre-wrap">{children}</Box>
              )
            ) : (
              children
            )}
          </Popover.Body>
        </PopoverContent>
      </Portal>
    </Popover.Root>
  );
};
